import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Loader2, Truck, Scale, BadgeCheck, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import BrandLogo from './BrandLogo';
import { availabilityOf } from '../services/stock';
import { productPath } from '../services/productPath';
import { compareStore, COMPARE_MAX } from '../services/compareStore';
import { cartAPI } from '../services/api';
import { useT } from '../services/i18n';
import ShareCompareButtons from './ShareCompareButtons';

const Stars = ({ rating }) => (
  <span className="inline-flex items-center gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={12} className={i < Math.round(rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />)}</span>
);

const deliveryOf = (p, t) => {
  const a = availabilityOf(p);
  if (a.status === 'out') return { text: t('equiv.outOfStock'), cls: 'text-red-600', days: 99 };
  if (a.status === 'supplier') return { text: t('equiv.supplierDelay', { n: a.lead }), cls: 'text-blue-700', days: a.lead };
  return { text: t('equiv.ship24'), cls: 'text-green-700', days: 1 };
};

// Side-by-side comparison of the viewed part and its catalogue equivalents (used inline on the product page)
const EquivalentsCompareTable = ({ current, items, onNavigate, testId = 'equivalents-compare' }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(null);
  const all = [current, ...items.filter(p => p.id !== current.id)];
  const cheapest = all.reduce((a, b) => (b.price < a.price ? b : a));
  const bestRated = all.filter(p => p.reviews > 0).sort((a, b) => b.rating - a.rating || b.reviews - a.reviews)[0];
  const fastest = all.map(p => ({ p, d: deliveryOf(p, t).days })).sort((a, b) => a.d - b.d)[0]?.p;
  const addToCart = async (id) => { setAdding(id); try { await cartAPI.addItem(id); } finally { setAdding(null); } };
  const openComparator = () => { compareStore.clear(); all.slice(0, COMPARE_MAX).forEach(p => compareStore.add(p)); onNavigate?.(); navigate('/comparaison'); };
  const Row = ({ label, render, rowId }) => (
    <tr className="border-t" data-testid={`${testId}-row-${rowId}`}>
      <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2.5 whitespace-nowrap bg-gray-50/60">{label}</th>
      {all.map(p => <td key={p.id} className={`px-3 py-2.5 text-sm align-top ${p.id === current.id ? 'bg-orange-50/40' : ''}`}>{render(p)}</td>)}
    </tr>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b flex-wrap">
        <div>
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Scale size={18} className="text-blue-600" /> {t('equiv.compareTitle')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('equiv.compareSub', { n: all.length })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ShareCompareButtons products={all} testId={`${testId}-share`} />
          <Button size="sm" variant="outline" onClick={openComparator} data-testid={`${testId}-open-page`} className="h-8 rounded-full text-xs">{t('equiv.openComparator')}</Button>
        </div>
      </div>
      <div className="overflow-auto">
        <table className="w-full min-w-[720px] table-fixed" data-testid={`${testId}-table`}>
          <thead>
            <tr>
              <th className="bg-gray-50/60 w-36" />
              {all.map(p => (
                <td key={p.id} className={`px-3 pt-4 pb-2 text-center ${p.id === current.id ? 'bg-orange-50/40' : ''}`} data-testid={`${testId}-col-${p.id}`}>
                  <Link to={productPath(p)} onClick={onNavigate}><img src={p.image} alt={p.name} className="h-24 mx-auto object-contain no-placeholder" /></Link>
                  <p className="text-sm font-medium text-gray-900 mt-2 line-clamp-2">{p.name}</p>
                  {p.id === current.id && <span className="text-[10px] uppercase tracking-wide text-orange-700 font-semibold">{t('equiv.current')}</span>}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label={t('equiv.colBrand')} rowId="brand" render={p => <span className="inline-flex items-center gap-1.5"><BrandLogo brand={p.brand} size="xs" showName={false} /> {p.brand}</span>} />
            <Row label={t('equiv.colRef')} rowId="ref" render={p => <span className="font-mono text-xs">{p.ref}</span>} />
            <Row label={t('equiv.colPrice')} rowId="price" render={p => (
              <div>
                <span className={`text-lg font-bold ${p.id === cheapest.id ? 'text-green-700' : 'text-gray-900'}`}>{p.price.toFixed(2)} €</span>
                {p.original_price && <span className="ml-1 text-xs text-gray-400 line-through">{p.original_price.toFixed(2)} €</span>}
                {p.id === cheapest.id && all.length > 1 && <span className="block text-[11px] text-green-700 mt-0.5" data-testid={`${testId}-cheapest-${p.id}`}><Trophy size={11} className="inline mr-1" />{t('equiv.cheapest')}</span>}
                {p.id !== cheapest.id && <span className="block text-[11px] text-gray-500" data-testid={`${testId}-diff-${p.id}`}>+{(p.price - cheapest.price).toFixed(2)} €</span>}
                {p.best_price?.active && <span className="block text-[11px] text-emerald-700 mt-0.5"><BadgeCheck size={11} className="inline mr-1" />{t('equiv.bestMarket')}</span>}
              </div>
            )} />
            <Row label={t('equiv.colRating')} rowId="rating" render={p => (
              <div><Stars rating={p.rating} /> <span className="text-xs text-gray-500">{p.rating ? p.rating.toFixed(1) : '—'} ({p.reviews || 0})</span>
                {bestRated && p.id === bestRated.id && all.length > 1 && <span className="block text-[11px] text-yellow-700">{t('equiv.bestRated')}</span>}</div>
            )} />
            <Row label={t('equiv.colDelivery')} rowId="delivery" render={p => { const d = deliveryOf(p, t); return <span className={`inline-flex items-center gap-1 text-xs font-medium ${d.cls}`}><Truck size={12} /> {d.text}{fastest && p.id === fastest.id && all.length > 1 && d.days < 99 ? ` · ${t('equiv.fastest')}` : ''}</span>; }} />
            <Row label={t('equiv.colStock')} rowId="stock" render={p => { const a = availabilityOf(p); return <span className="text-xs text-gray-700">{t('equiv.stockLine', { w: a.warehouse })}{a.supplier > 0 ? t('equiv.stockSupplier', { s: a.supplier }) : ''}</span>; }} />
            <Row label={t('equiv.colType')} rowId="type" render={p => <span className="text-xs text-gray-600">{p.type || '—'}</span>} />
            <Row label="" rowId="actions" render={p => (
              <div className="flex flex-col gap-1.5">
                {availabilityOf(p).status === 'out' ? <span className="text-xs text-gray-400">{t('equiv.unavailable')}</span> : (
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-xs" onClick={() => addToCart(p.id)} disabled={adding === p.id} data-testid={`${testId}-add-${p.id}`}>
                    {adding === p.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <ShoppingCart size={14} className="mr-1" />} {t('common.addToCart')}
                  </Button>
                )}
                {p.id !== current.id && <Link to={productPath(p)} onClick={onNavigate} className="text-xs text-blue-700 hover:underline text-center" data-testid={`${testId}-view-${p.id}`}>{t('equiv.viewProduct')}</Link>}
              </div>
            )} />
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EquivalentsCompareTable;
